import React, { useState, useEffect } from "react";
import {
    Grid,
    TextField,
    IconButton,
    Autocomplete,
    Popover,
    Box,
    Typography,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import FlightIcon from "@mui/icons-material/Flight";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const MultiCityForm = () => {
    const [flights, setFlights] = useState([
        { id: Date.now(), from: "", to: "", depart: "" },
    ]);
    const [fromInputs, setFromInputs] = useState({});
    const [toInputs, setToInputs] = useState({});
    const [fromOptions, setFromOptions] = useState({});
    const [toOptions, setToOptions] = useState({});
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    const [infants, setInfants] = useState(0);
    const [classType, setClassType] = useState("2");
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [discountType, setDiscountType] = useState("0");
    const directFlight = false;
    const oneStopFlight = false;
    const [fareQuote, setfareQuote] = useState({});

    const classTypeNames = {
        1: "All",
        2: "Economy",
        3: "Premium Economy",
        4: "Business",
        5: "Premium Business",
        6: "First Class",
    };

    const discountFareMap = {
        regular: "2",
        student: "3",
        armedForces: "4",
        seniorCitizen: "5",
    };

    const handleFlightInputChange = (id, field, value) => {
        setFlights((prev) =>
            prev.map((flight) =>
                flight.id === id ? { ...flight, [field]: value } : flight
            )
        );
    };

    const handleAddFlight = () => {
        const newId = Date.now();
        setFlights([...flights, { id: newId, from: "", to: "", depart: "" }]);
    };

    const handleRemoveFlight = (id) => {
        setFlights(flights.filter((flight) => flight.id !== id));
    };

    const handleIncrement = (setter, value) => setter(value + 1);
    const handleDecrement = (setter, value) => {
        if (value > 0) setter(value - 1);
    };

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const open = Boolean(anchorEl);

    const handleMultiCityFlightSearch = async () => {
        const jwtToken = sessionStorage.getItem("jwtToken");
        if (!jwtToken) {
            alert("Authentication token missing. Please log in.");
            toast.error("Authentication token missing. Please log in.");
            return;
        }

        const segments = flights.map((flight) => ({
            Origin: flight.from,
            Destination: flight.to,
            FlightCabinClass: classType.toString(),
            PreferredDepartureTime: `${flight.depart}T00:00:00`,
            PreferredArrivalTime: `${flight.depart}T00:00:00`,
        }));

        const searchDetails = {
            from: flights[0]?.from || "",
            to: flights[0]?.to || "",
            departDate: flights[0]?.depart || "",
            travelers: adults + children + infants || "",
            travelClass: classTypeNames[classType],
            adults,
            children,
            infants,
        };

        const payload = {
            AdultCount: adults.toString(),
            ChildCount: children.toString(),
            InfantCount: infants.toString(),
            DirectFlight: directFlight.toString(),
            OneStopFlight: oneStopFlight.toString(),
            JourneyType: "3",
            ResultFareType: discountFareMap[discountType],
            Segments: segments,
            page: page,
            pageSize: 10,
        };

        setLoading(true);

        try {
            const response = await axios.post(
                "https://api.ipaisa.co.in/myapp/flight/search",
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${jwtToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response?.data?.Response?.Error?.ErrorCode === 0) {
                toast.success("Flights retrieved successfully!");
                const flightsData = response.data;
                navigate("/multiflightresult", {
                    state: { flightsData, searchDetails },
                });
            } else {
                const errorMsg = response.data.Response.Error.ErrorMessage || "Something went wrong, Please try again!";
                alert(errorMsg);
                toast.warn(errorMsg);
            }
        } catch (error) {
            console.error("Error fetching multi-city flights:", error);
            alert("Failed to fetch flight data. Please try again after sometime.");
            toast.error("Failed to fetch flight data. Please try again after sometime.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAirports = async (searchTerm, type, id) => {
            if (!searchTerm) return;
            try {
                const apiType = type === "from" ? "origin" : "destination";
                const response = await axios.get(
                    `https://api.ipaisa.co.in/myapp/flight/citysearch?searchTerm=${encodeURIComponent(searchTerm)}&type=${apiType}`
                );
                if (response.data?.status === "success") {
                    const options = response.data.airports.map((airport) => ({
                        label: `${airport.CityCode} - ${airport.Place} (${airport.AirportName})`,
                        cityCode: airport.CityCode,
                    }));
                    if (type === "from") {
                        setFromOptions((prev) => ({ ...prev, [id]: options }));
                    } else {
                        setToOptions((prev) => ({ ...prev, [id]: options }));
                    }
                }
            } catch (error) {
                console.error("Error fetching airports:", error);
            }
        };

        flights.forEach(({ id }) => {
            fetchAirports(fromInputs[id], "from", id);
            fetchAirports(toInputs[id], "to", id);
        });
    }, [fromInputs, toInputs, flights]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item md={7} xs={12}>
                    {flights.map((flight, index) => (
                        <Grid container spacing={2} key={flight.id} alignItems="center" sx={{ mb: 1 }}>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    options={fromOptions[flight.id] || []}
                                    getOptionLabel={(option) => option.label || ""}
                                    onInputChange={(e, value) => setFromInputs({ ...fromInputs, [flight.id]: value })}
                                    onChange={(e, selectedOption) =>
                                        selectedOption && handleFlightInputChange(flight.id, "from", selectedOption.cityCode)
                                    }
                                    renderInput={(params) => <TextField {...params} label="From" variant="outlined" />}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    options={toOptions[flight.id] || []}
                                    getOptionLabel={(option) => option.label || ""}
                                    onInputChange={(e, value) => setToInputs({ ...toInputs, [flight.id]: value })}
                                    onChange={(e, selectedOption) =>
                                        selectedOption && handleFlightInputChange(flight.id, "to", selectedOption.cityCode)
                                    }
                                    renderInput={(params) => <TextField {...params} label="To" variant="outlined" />}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Depart"
                                    value={flight.depart}
                                    onChange={(e) => handleFlightInputChange(flight.id, "depart", e.target.value)}
                                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {index > 0 && (
                                <Grid item xs={12} md={1}>
                                    <IconButton onClick={() => handleRemoveFlight(flight.id)} color="error">
                                        <CloseIcon />
                                    </IconButton>
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Grid>

                <Grid item md={5} xs={12} mt={2}>
                    <TextField
                        fullWidth
                        label="Travelers"
                        value={`${adults} Adults, ${children} Children, ${infants} Infants, ${classTypeNames[classType]}`}
                        onClick={handleOpen}
                        InputProps={{ endAdornment: <ArrowDropDownIcon />, readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Popover open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                        <Box sx={{ p: 2, width: "250px" }}>
                            {[{ label: "Adults", value: adults, setter: setAdults }, { label: "Children", value: children, setter: setChildren }, { label: "Infants", value: infants, setter: setInfants }].map(({ label, value, setter }) => (
                                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Typography>{label}</Typography>
                                    <Box>
                                        <IconButton onClick={() => handleDecrement(setter, value)} size="small"><RemoveIcon /></IconButton>
                                        <Typography component="span" sx={{ mx: 1 }}>{value}</Typography>
                                        <IconButton onClick={() => handleIncrement(setter, value)} size="small"><AddIcon /></IconButton>
                                    </Box>
                                </Box>
                            ))}
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Class</Typography>
                            <RadioGroup value={classType} onChange={(e) => setClassType(e.target.value)}>
                                {Object.entries(classTypeNames).map(([key, value]) => (
                                    <FormControlLabel key={key} value={key} control={<Radio />} label={value} />
                                ))}
                            </RadioGroup>
                        </Box>
                    </Popover>
                </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                <Button variant="outlined" onClick={handleAddFlight}>+ Add Another Flight</Button>
                <Button startIcon={<FlightIcon />} onClick={handleMultiCityFlightSearch} disabled={loading} sx={{ background: "#027BFE", color: "white" }}>
                    {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Show Flights"}
                </Button>
            </Box>

            {searchResults && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6">Search Results</Typography>
                    <pre style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(searchResults, null, 2)}</pre>
                </Box>
            )}
        </>
    );
};

export default MultiCityForm;